import React, { useEffect, useState } from 'react';
import { toast } from '../../../../ui/toast';
import { Button } from '../../../../ui';
import { CloseIcon, UploadIcon } from '../../../../ui/icons/icons';
import { putJob, uploadFile } from '../../../../utils/api_service/job_api';
import { formatDateTime } from '../../../../utils/helpers/time';
import {
  ContextBasedGeneratorJob,
  Settings,
} from '../../../../utils/types/content_manager_types';
import { GenerateProperties } from '../GenerateProperties/GenerateProperties';
import { SessionTitle } from '../session_title';
import { TopicInput } from '../topic_input';

type UploadDocumentDropboxProps = {
  uploadedFile: File | null;
  setUploadedFile: (file: File | null) => void;
  pollJobStatus: (id: string, generateQuestions?: boolean) => void;
  setJob: (job: ContextBasedGeneratorJob) => void;
  setQuestionLoading: (status: boolean) => void;
  questionLoading: boolean;
  job: ContextBasedGeneratorJob;
};

export const UploadDocumentDropbox = ({
  job,
  uploadedFile,
  setUploadedFile,
  pollJobStatus,
  setJob,
  setQuestionLoading,
  questionLoading,
}: UploadDocumentDropboxProps) => {
  const [topic, setTopic] = useState<string>('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [defaultSessionTitle, setdefaultSessionTitle] = useState<string>(
    `${topic ? topic : ''}-`,
  );
  const [sessionTitle, setSessionTitle] = useState<string>(defaultSessionTitle);
  const [editGenerateProperties, setEditGenerateProperties] =
    useState<Settings>({
      level: '',
      jobTitle: '',
      questionType: '',
      time: 30,
      points: 5,
    });

  useEffect(() => {
    const currentDateTimeString = formatDateTime(new Date());
    const titlePrefix = topic ? topic : 'Question generation';
    setdefaultSessionTitle(`${titlePrefix} - ${currentDateTimeString}`);
  }, [topic]);

  const dragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const dragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const dragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const fileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    const files = event.dataTransfer && event.dataTransfer.files;
    if (files.length && validateFile(files[0])) {
      handleFiles(files);
    }
  };

  const addFileViaInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && validateFile(files[0])) {
      handleFiles(files);
    }
  };

  const validateFile = (file: File | null) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/pdf',
      'application/msword',
      'application/vnd.ms-powerpoint',
      'text/plain',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/webp',
      'text/html',
    ];
    if (file) {
      return validTypes.indexOf(file.type) !== -1;
    } else {
      return false;
    }
  };

  const editProperties = (
    job_id: string,
    response_job: ContextBasedGeneratorJob,
  ) => {
    const updatedJobWithSettings = {
      ...response_job,
      settings: editGenerateProperties,
    };
    putJob(job_id, updatedJobWithSettings).then((response) => {
      setJob(response);
    });
  };

  const fileSize = (size: number) => {
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFiles = (files: FileList) => {
    const file = files.item(0);
    setUploadedFile(file);
  };

  const doUploadFile = () => {
    setQuestionLoading(true);
    if (uploadedFile !== null && topic !== undefined) {
      uploadFile(uploadedFile, topic, sessionTitle)
        .then((response) => {
          pollJobStatus(response.id);
          editProperties(response.id, response);
        })
        .catch(() => {
          toast.error('Error. Please try again', { position: 'bottom-center' });
          setQuestionLoading(false);
        });
    }
  };

  const acceptedFormats =
    'DOCX, PDF, PPT, DOC, JPEG, PNG, GIF, BMP, WEBP, HTML, TXT';

  return (
    <div className='space-y-3'>
      <SessionTitle
          sessionTitle={sessionTitle}
          setSessionTitle={setSessionTitle}
          defaultValue={defaultSessionTitle}
          disabled={questionLoading}
      />

      <div className='rounded-lg border border-slate-200 bg-white p-3 shadow-sm'>
        <div className='mb-2'>
          <p className='text-sm font-extrabold text-slate-900'>
            Content Topic
          </p>
          <p className='text-xs text-slate-500'>
            Enter one main skill, then optional sub-skills with Enter.
          </p>
        </div>
        <TopicInput
            setTopic={setTopic}
            disabled={questionLoading}
            placeholder='Type a skill and press Enter...'
        />
      </div>

      <GenerateProperties
          setEditGenerateProperties={setEditGenerateProperties}
          job={job}
      />

      <input
          type='file'
          onChange={addFileViaInput}
          id='upload-file-input'
          style={{ display: 'none' }}
      />

      <section className='rounded-lg border border-slate-200 bg-white p-3 shadow-sm'>
        <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
          <div>
            <p className='text-sm font-extrabold text-slate-900'>
              Upload Document
            </p>
            <p className='text-xs text-slate-500'>
              Auto-generate questions by uploading a source document.
            </p>
          </div>
          <div className='flex flex-wrap gap-1.5 text-[11px]'>
            <span className='rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-600'>
              Max file size: 2 GB
            </span>
            <span className='rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-600'>
              Formats: { acceptedFormats }
            </span>
          </div>
        </div>

        <div className='mt-3 grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_300px]'>
          <div
              onDragOver={dragOver}
              onDragEnter={dragEnter}
              onDragLeave={dragLeave}
              onDrop={fileDrop}
              className={`rounded-lg border-2 border-dashed px-3 py-5 text-center transition-all duration-200 ${
                isDragActive
                  ? 'border-emerald-500 bg-emerald-50/70'
                  : 'border-slate-300 bg-white/80 hover:border-emerald-400'
              }`}
          >
            <div className='mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600'>
              <UploadIcon />
            </div>
            <p className='text-sm font-medium text-slate-700'>
              Drag and drop your file here
            </p>
            <p className='mt-1 text-xs text-slate-500'>
              or choose a file manually from your device
            </p>
            <div className='mt-3'>
              <Button
                  onClick={() =>
                    document.getElementById('upload-file-input')?.click()
                  }
                  variant='primary'
                  label='Browse Files'
              />
            </div>
          </div>

          <div className='rounded-lg border border-slate-200 bg-slate-50/60 p-3'>
            <p className='text-sm font-semibold text-slate-700'>
              Selected File
            </p>
            { uploadedFile ? (
              <div className='mt-2 space-y-1.5'>
                <div className='flex items-start justify-between gap-2 rounded-md bg-slate-50 p-2'>
                  <p className='truncate text-sm font-medium text-slate-700'>
                    { uploadedFile.name }
                  </p>
                  <button
                      type='button'
                      className='text-slate-500 hover:text-red-500 transition-colors'
                      onClick={() => setUploadedFile(null)}
                      aria-label='Remove selected file'
                  >
                    <CloseIcon />
                  </button>
                </div>
                <p className='text-xs text-slate-500'>
                  Size: { fileSize(uploadedFile.size) }
                </p>
                <p className='text-xs text-slate-500'>
                  Allowed formats: { acceptedFormats }
                </p>
              </div>
            ) : (
              <div className='mt-2 rounded-md border border-slate-200 border-dashed p-2.5 text-xs text-slate-500'>
                No file selected yet.
              </div>
            ) }
          </div>
        </div>

        <div className='mt-3 flex justify-end border-t border-slate-100 pt-3'>
          <Button
              onClick={doUploadFile}
              variant='primary'
              label='Generate Questions'
              disabled={
                topic === undefined ||
              topic.length === 0 ||
              uploadedFile === null ||
              questionLoading ||
              (job.questions && job.questions?.length > 0) ||
              editGenerateProperties.jobTitle === ''
              }
          />
        </div>
      </section>
    </div>
  );
};
